/**
 * i18n bootstrap.
 *
 * Two responsibilities:
 *  1. Register an EN + ES resource dictionary with `i18next` so components
 *     can call `t("…")` to render translated copy.
 *  2. Keep i18next's active language in sync with the authenticated user's
 *     `primary_language` setting (see `syncLanguageWithUser` below).
 *
 * The dictionary stays small on purpose: we translate the UI chrome the
 * user sees most (sidebar, gratitude wall, profile settings) and let the
 * rest fall through to the English key. That matches our phased rollout —
 * additional strings get added as we touch each page.
 */
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import en from "./en.json";
import es from "./es.json";

if (!i18n.isInitialized) {
  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: {
        en: { common: en },
        es: { common: es },
      },
      fallbackLng: "en",
      defaultNS: "common",
      interpolation: {
        // React already escapes output — no double escaping needed.
        escapeValue: false,
      },
      detection: {
        // Respect what the profile endpoint said last. We store the choice
        // under `evoluras-lang` so it persists before the profile response
        // arrives on next load (avoids a flash of English).
        order: ["localStorage", "navigator", "htmlTag"],
        caches: ["localStorage"],
        lookupLocalStorage: "evoluras-lang",
      },
      returnEmptyString: false,
    });
}

/**
 * Keep i18n in lockstep with the user's server-side primary language.
 * Call this from wherever `useAuth` exposes the profile — passing `null`
 * leaves the current setting untouched (so guests keep whatever the
 * language detector picked).
 */
export const syncLanguageWithUser = (user) => {
  const code = user?.primary_language;
  if (!code) return;
  if (i18n.language === code) return;
  i18n.changeLanguage(code);
  try {
    localStorage.setItem("evoluras-lang", code);
  } catch {
    /* quota / private-mode — harmless */
  }
};

export default i18n;
